import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Collapse, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import React, { useState } from 'react';
import { SubmitHandler, useFormContext, useWatch } from 'react-hook-form';

import { SortDirectionDto } from '@/api/generated';
import { DateFormField } from '@/components/form/DateFormField.comp.tsx';
import { SelectSearchFormField } from '@/components/form/SelectSearchFormField.comp';
import { TextFormField } from '@/components/form/TextFormField.comp';
import { CustomTableAdditionalButtonsModel } from '@/components/table/components/formBuilder/model/CustomTableAdditionalButtons.model.ts';
import { CustomTableFilterDefinition } from '@/components/table/components/formBuilder/model/CustomTableFilterDefinition.model.ts';
import { CustomTableFilterTypeEnum } from '@/components/table/components/formBuilder/model/CustomTableFilterTypeEnum.ts';
import { countActiveFilters } from '@/components/table/components/formBuilder/util/activeFiltersCount.util.ts';
import { useCustomTableContext } from '@/components/table/context/CustomTable.context';
import { CustomTableVariant } from '@/components/table/CustomTable.comp.tsx';
import { CustomTableStateDataModel } from '@/components/table/model/CustomTableState.model.ts';
import { CustomTableQuickFilter } from '@/components/table/quickFilters/CustomTableQuickFilter.model.ts';
import { CustomTableQuickFilters } from '@/components/table/quickFilters/CustomTableQuickFilters.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { AnnouncementAnchor } from '@/contexts/announcements/AnnouncementAnchor.comp.tsx';
import { LocalStorageKey, localStorageUtil } from '@/utils/localStorage/localStorage.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

interface BasicRequestProps<
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
> {
  page: number;
  pageSize: number;
  params: SEARCH_FORM_MODEL;
  sortBy?: SORT_BY_COLUMN_NAME_MODEL;
  sortOrder?: SortDirectionDto;
}

interface Props<
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
> {
  additionalButtons: CustomTableAdditionalButtonsModel<SEARCH_FORM_MODEL, BUTTONS_PARAMS>[];
  filters: CustomTableFilterDefinition<SEARCH_FORM_MODEL>[];
  formRequest: (
    req: BasicRequestProps<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL>,
  ) => Promise<CustomTableStateDataModel<ROW_ITEM_MODEL>>;
  initParams: BasicRequestProps<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL>;
  isLoading: boolean;
  quickFilters?: CustomTableQuickFilter<SEARCH_FORM_MODEL>[];
  size?: CustomTableVariant;
  tableKey: string;
}

const SEARCH_FIELD_NAME = 'fullTextSearchInput';

export const CustomTableFormBuilder = <
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
>({
  additionalButtons,
  filters,
  formRequest,
  initParams,
  isLoading,
  quickFilters,
  size,
  tableKey,
}: Props<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL, BUTTONS_PARAMS>) => {
  const storageKey = { key: LocalStorageKey.CUSTOM_TABLE_FILTERS_OPEN, subKey: tableKey };
  const [areFiltersOpen, setAreFiltersOpen] = useState<boolean>(
    () => localStorageUtil.getItem<boolean>(storageKey) ?? !quickFilters?.length,
  );
  const { customTableState, setCustomTableState } = useCustomTableContext<
    SORT_BY_COLUMN_NAME_MODEL,
    SEARCH_FORM_MODEL,
    ROW_ITEM_MODEL
  >();
  const formMethods = useFormContext<SEARCH_FORM_MODEL>();
  const formValues = useWatch({ control: formMethods.control }) as Partial<SEARCH_FORM_MODEL>;
  const { t } = useTranslationWithPrefix('components.table.content');

  const handleSubmitWrapper = async () => {
    await formMethods.handleSubmit(handleSubmit)();
  };

  const handleSubmit: SubmitHandler<SEARCH_FORM_MODEL> = async from => {
    const data = await formRequest({
      ...customTableState,
      page: 0,
      params: from,
    });

    setCustomTableState(cur => ({
      ...cur,
      data: {
        ...data,
      },
      page: 0,
      params: formMethods.getValues(),
    }));
  };

  const handleResetForm = async () => {
    formMethods.reset(initParams.params);

    const data = await formRequest({
      ...customTableState,
      page: 0,
      params: initParams.params,
      sortBy: initParams.sortBy,
      sortOrder: SortDirectionDto.Asc,
    });

    setCustomTableState(cur => ({
      ...cur,
      data: {
        ...data,
      },
      page: 0,
      params: initParams.params,
      sortBy: initParams.sortBy!,
      sortOrder: SortDirectionDto.Asc,
    }));
  };

  const handleToggleFilters = () => {
    const next = !areFiltersOpen;
    localStorageUtil.setItem({ ...storageKey, value: next });
    setAreFiltersOpen(next);
  };

  const handleSearchOnEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      void handleSubmitWrapper();
    }
  };

  const resolveField = (field: CustomTableFilterDefinition<SEARCH_FORM_MODEL>) => {
    const { announcementAnchorKey, disabled, type } = field;
    const isDisabled = disabled ?? isLoading;
    let resolvedField: React.ReactNode;

    if (type === CustomTableFilterTypeEnum.Text) {
      resolvedField = (
        <Box onKeyDown={handleSearchOnEnter}>
          <TextFormField {...field} disabled={isDisabled} />
        </Box>
      );
    }
    if (type === CustomTableFilterTypeEnum.Select) {
      resolvedField = <SelectSearchFormField {...field} disabled={isDisabled} />;
    }
    if (type === CustomTableFilterTypeEnum.Date) {
      resolvedField = <DateFormField {...field} disabled={isDisabled} />;
    }
    if (!announcementAnchorKey) {
      return resolvedField;
    }

    return (
      <AnnouncementAnchor
        anchorKey={announcementAnchorKey}
        component="div"
        sx={{ display: 'block', lineHeight: 0 }}
      >
        {resolvedField}
      </AnnouncementAnchor>
    );
  };

  const searchField = filters.find(
    field => field.type === CustomTableFilterTypeEnum.Text && field.name === SEARCH_FIELD_NAME,
  );
  const activeFiltersCount = countActiveFilters(filters, formValues);
  const additionalButtonsNodes = additionalButtons.map(additionalButton => (
    <additionalButton.button
      handleGetValues={formMethods.getValues}
      isLoading={isLoading}
      key={additionalButton.name}
      params={additionalButton.params}
    />
  ));

  return (
    <>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', columnGap: 2, flexWrap: 'wrap', pt: 1, rowGap: 1 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {quickFilters && quickFilters.length > 0 && (
            <CustomTableQuickFilters<SEARCH_FORM_MODEL>
              appliedParams={customTableState.params}
              filters={quickFilters}
              isLoading={isLoading}
              onApply={handleSubmitWrapper}
            />
          )}
        </Box>
        {!areFiltersOpen && searchField && (
          <Box onKeyDown={handleSearchOnEnter} sx={{ width: { md: 280, xs: '100%' } }}>
            <TextFormField
              disabled={searchField.disabled ?? isLoading}
              label=""
              name={searchField.name}
              placeholder={searchField.label}
              StartIcon={<SearchIcon sx={{ color: themeColors.grey, fontSize: 18 }} />}
            />
          </Box>
        )}
        {!areFiltersOpen && additionalButtonsNodes}
        <Tooltip
          title={activeFiltersCount > 0 ? t('activeFilters', { count: activeFiltersCount }) : ''}
        >
          <Button
            aria-expanded={areFiltersOpen}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: areFiltersOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms ease',
                }}
              />
            }
            onClick={handleToggleFilters}
            size="small"
            startIcon={<FilterListIcon />}
            sx={{ color: themeColors.accentColorDark, flexShrink: 0 }}
            variant="outlined"
          >
            {areFiltersOpen ? t('button.hideFilters') : t('button.showFilters')}
            {activeFiltersCount > 0 && (
              <Box
                component="span"
                sx={{
                  backgroundColor: themeColors.accentColor,
                  borderRadius: '999px',
                  color: themeColors.white,
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: '18px',
                  minWidth: 18,
                  ml: 0.75,
                  px: 0.5,
                  textAlign: 'center',
                }}
              >
                {activeFiltersCount}
              </Box>
            )}
          </Button>
        </Tooltip>
      </Stack>
      <Collapse in={areFiltersOpen} timeout="auto" unmountOnExit>
        <Grid container spacing={size === 'small' ? 1 : 2} sx={{ pt: 1 }}>
          {filters.map(field => (
            <Grid key={field.name} size={field.xs}>
              {resolveField(field)}
            </Grid>
          ))}
        </Grid>
        <Stack
          direction="row"
          spacing={size === 'small' ? 1 : 2}
          sx={{ justifyContent: 'flex-end', pt: size === 'small' ? 1 : 2 }}
        >
          {additionalButtonsNodes}
          <Button disabled={isLoading} onClick={handleResetForm} variant="outlined">
            {t('button.resetForm')}
          </Button>
          <Button disabled={isLoading} onClick={handleSubmitWrapper}>
            {t('button.submitSearch')}
          </Button>
        </Stack>
      </Collapse>
    </>
  );
};
